import { uid } from './ids.ts';
import type { ColorName, SelectOption } from './model.ts';
import { RARITY_COLORS } from './model.ts';
import type { NewCollection, NewField, NewView } from './project-doc.ts';
import { singularize } from './words.ts';

/**
 * Collection presets: the schemas every project starts with (mechanics, ideas, tasks and assets) and
 * the codex of the Emberfall sample. A game's own collections start from scratch
 * (`scratchCollection`). Every preset is a function so each call gets fresh ids; pass `links` to
 * wire relation fields to other collections.
 */

export interface PresetLinks {
  assets?: string;
  abilities?: string;
}

export const opt = (label: string, color: ColorName, id = uid(8)): SelectOption => ({ id, label, color });
const opts = (...pairs: [string, ColorName][]) => pairs.map(([l, c]) => opt(l, c));

export const rarityOptions = () =>
  opts(
    ['Common', RARITY_COLORS.common],
    ['Uncommon', RARITY_COLORS.uncommon],
    ['Rare', RARITY_COLORS.rare],
    ['Epic', RARITY_COLORS.epic],
    ['Legendary', RARITY_COLORS.legendary],
  );

const f = (name: string, type: NewField['type'], config: NewField['config'] = {}, extra: Partial<NewField> = {}): NewField & { id: string } => ({
  id: uid(),
  name,
  type,
  config,
  ...extra,
});

const bar = (max: number, extra: NewField['config'] = {}) => ({ display: 'bar' as const, max, min: 0, ...extra });
const rel = (target: string | undefined, multiple = true) => ({ targetCollectionId: target ?? null, multiple });

/* ----------------------------------------------------------------------------------------------
 * The ready-made collections a new project can start with
 * -------------------------------------------------------------------------------------------- */

export function mechanicsCollection(name = 'Mechanics', extra: Partial<NewCollection> = {}): NewCollection {
  const status = f('Status', 'select', { options: opts(['Draft', 'gray'], ['In review', 'yellow'], ['Approved', 'green'], ['Deprecated', 'red']) }, { role: 'status' });
  const owner = f('Owner', 'person', { multiple: false }, { role: 'owner' });
  const tags = f('Tags', 'multiSelect', { options: opts(['Core', 'blue'], ['Combat', 'red'], ['Economy', 'yellow'], ['Progression', 'green'], ['Tech', 'purple']) }, { role: 'tags' });
  return {
    name,
    icon: 'i:scroll-text',
    color: 'purple',
    titleLabel: 'Doc',
    description: 'Design documents: rules, systems, formulas and the reasoning behind them.',
    fields: [status, owner, tags],
    views: [
      { name: 'All docs', type: 'list', sort: [{ fieldId: '_updated', dir: 'desc' }] },
      { name: 'By status', type: 'board', groupBy: status.id },
    ],
    ...extra,
  };
}

export function ideasCollection(extra: Partial<NewCollection> = {}): NewCollection {
  const status = f('Status', 'select', { options: opts(['New', 'blue'], ['Discussing', 'yellow'], ['Accepted', 'green'], ['Parked', 'gray'], ['Rejected', 'red']) }, { role: 'status' });
  const votes = f('Votes', 'votes', { allowDownvotes: false }, { role: 'votes' });
  const tags = f('Area', 'multiSelect', { options: opts(['Gameplay', 'orange'], ['Art', 'pink'], ['Audio', 'teal'], ['Narrative', 'purple'], ['Tech', 'blue'], ['UX', 'green']) }, { role: 'tags' });
  const impact = f('Impact', 'rating', { max: 5 });
  const effort = f('Effort', 'select', { options: opts(['S', 'green'], ['M', 'yellow'], ['L', 'orange'], ['XL', 'red']) });
  return {
    name: 'Ideas',
    icon: 'i:lightbulb',
    color: 'yellow',
    titleLabel: 'Idea',
    description: 'Every idea, big or small. Vote on them, discuss, and promote the best ones to tasks.',
    fields: [status, votes, tags, impact, effort],
    views: [
      { name: 'Board', type: 'board', groupBy: status.id },
      { name: 'Top voted', type: 'table', sort: [{ fieldId: votes.id, dir: 'desc' }] },
    ],
    ...extra,
  };
}

export function tasksCollection(extra: Partial<NewCollection> = {}): NewCollection {
  const status = f('Status', 'select', { options: opts(['Backlog', 'gray'], ['To do', 'blue'], ['In progress', 'yellow'], ['Review', 'purple'], ['Done', 'green']) }, { role: 'status' });
  const priority = f('Priority', 'select', { options: opts(['Urgent', 'red'], ['High', 'orange'], ['Medium', 'yellow'], ['Low', 'gray']) }, { role: 'priority' });
  const assignee = f('Assignee', 'person', { multiple: false }, { role: 'assignee' });
  const due = f('Due', 'date', {}, { role: 'due' });
  const labels = f('Labels', 'multiSelect', { options: opts(['Design', 'purple'], ['Art', 'pink'], ['Code', 'blue'], ['Audio', 'teal'], ['Writing', 'brown'], ['QA', 'green']) }, { role: 'tags' });
  const estimate = f('Estimate', 'number', { suffix: 'h' });
  const related = f('Related', 'relation', rel(undefined));
  return {
    name: 'Tasks',
    icon: 'i:square-check',
    color: 'blue',
    titleLabel: 'Task',
    description: 'Production work linked to the design it implements.',
    fields: [status, priority, assignee, due, labels, estimate, related],
    views: [
      { name: 'Board', type: 'board', groupBy: status.id },
      { name: 'List', type: 'table', sort: [{ fieldId: priority.id, dir: 'asc' }] },
    ],
    ...extra,
  };
}

export function assetsCollection(extra: Partial<NewCollection> = {}): NewCollection {
  // The art itself is the record's cover; this field is for the rest — audio, source files, refs.
  const file = f('Files', 'file');
  const status = f('Status', 'select', { options: opts(['Concept', 'gray'], ['WIP', 'yellow'], ['Review', 'purple'], ['Final', 'green']) }, { role: 'status' });
  const tags = f('Tags', 'multiSelect', { options: opts(['Character', 'orange'], ['Environment', 'green'], ['UI', 'blue'], ['SFX', 'teal'], ['Music', 'purple'], ['VFX', 'pink'], ['Concept', 'gray']) }, { role: 'tags' });
  const artist = f('Artist', 'person', { multiple: false });
  const usedIn = f('Used in', 'relation', rel(undefined));
  return {
    name: 'Assets',
    icon: 'i:image',
    color: 'pink',
    titleLabel: 'Name',
    description: 'Art, audio and reference files with their production status.',
    fields: [file, status, tags, artist, usedIn],
    views: [
      { name: 'Gallery', type: 'gallery' },
      { name: 'Pipeline', type: 'board', groupBy: status.id },
      { name: 'Table', type: 'table' },
    ],
    ...extra,
  };
}

/* ----------------------------------------------------------------------------------------------
 * A game's own collections, made from scratch
 * -------------------------------------------------------------------------------------------- */

/** New collections take these colors in turn, so the sidebar stays easy to scan. */
export const COLLECTION_COLORS: ColorName[] = ['orange', 'blue', 'green', 'purple', 'red', 'teal', 'yellow', 'pink'];

/** The Status field a board made from scratch groups by: To do, In progress, Done. */
export function statusField(): NewField & { id: string } {
  return f('Status', 'select', { options: opts(['To do', 'gray'], ['In progress', 'yellow'], ['Done', 'green']) }, { role: 'status' });
}

/**
 * A collection made from scratch: each game is different, so there are no ready-made schemas. The
 * only field it comes with is the Status a board needs to make columns from.
 *
 * One record is named after the collection: "Recipes" → "Recipe" (it follows renames too).
 */
export function scratchCollection(
  name: string,
  {
    icon = 'i:box',
    color = COLLECTION_COLORS[0]!,
    groupId = null,
    views = [{ name: 'Table', type: 'table' }],
  }: { icon?: string; color?: ColorName; groupId?: string | null; views?: NewView[] } = {},
): NewCollection {
  const status = views.some((v) => v.type === 'board') ? statusField() : null;
  return {
    name: name.trim(),
    icon,
    color,
    groupId,
    titleLabel: singularize(name) || 'Name',
    fields: status ? [status] : [],
    views: views.map((v) => ({ ...v, groupBy: v.groupBy ?? (v.type === 'board' ? status!.id : null) })),
  };
}

/* ----------------------------------------------------------------------------------------------
 * The Emberfall sample's codex
 * -------------------------------------------------------------------------------------------- */

const galleryAndTable = (cover?: string, extra: NewView[] = []): NewView[] => [
  { name: 'Table', type: 'table' },
  { name: 'Cards', type: 'gallery', coverFieldId: cover ?? null },
  ...extra,
];

export function unitsCollection(links: PresetLinks = {}, factions = opts(['Faction A', 'orange'], ['Faction B', 'blue'])): NewCollection {
  const faction = f('Faction', 'select', { options: factions });
  const role = f('Role', 'select', { options: opts(['Melee', 'red'], ['Ranged', 'orange'], ['Caster', 'purple'], ['Support', 'green'], ['Siege', 'brown'], ['Hero', 'yellow']) });
  const tier = f('Tier', 'select', { options: opts(['T1', 'gray'], ['T2', 'green'], ['T3', 'blue'], ['Hero', 'purple']) });
  const hp = f('HP', 'number', bar(2500));
  const dmg = f('Damage', 'number', {});
  const as = f('Attack speed', 'number', { decimals: 2, suffix: '/s' });
  const dps = f('DPS', 'formula', { decimals: 1 });
  dps.config = { ...dps.config, expression: `{${dmg.id}} * {${as.id}}` };
  const armor = f('Armor', 'number', bar(12));
  const move = f('Move speed', 'number', { decimals: 1 });
  const range = f('Range', 'number', {});
  const cost = f('Gold cost', 'number', { suffix: ' g' });
  const build = f('Build time', 'number', { suffix: ' s' });
  const abilities = f('Abilities', 'relation', rel(links.abilities));
  const portrait = f('Portrait', 'relation', rel(links.assets, false));
  return {
    name: 'Units',
    icon: 'i:swords',
    color: 'orange',
    titleLabel: 'Unit',
    description: 'Every unit with its combat stats, cost and abilities.',
    fields: [faction, role, tier, hp, dmg, as, dps, armor, move, range, cost, build, abilities, portrait],
    views: [
      { name: 'Stats', type: 'table', hiddenFields: [portrait.id] },
      { name: 'Roster', type: 'gallery', coverFieldId: portrait.id },
      { name: 'By faction', type: 'board', groupBy: faction.id, coverFieldId: portrait.id },
    ],
  };
}

export function abilitiesCollection(links: PresetLinks = {}): NewCollection {
  const type = f('Type', 'select', { options: opts(['Active', 'blue'], ['Passive', 'gray'], ['Ultimate', 'purple']) });
  const element = f('Element', 'select', { options: opts(['Fire', 'orange'], ['Frost', 'blue'], ['Nature', 'green'], ['Arcane', 'pink'], ['Physical', 'gray']) });
  const damage = f('Damage', 'range', {});
  const cooldown = f('Cooldown', 'number', { suffix: ' s' });
  const mana = f('Mana', 'number', {});
  const radius = f('Radius', 'number', {});
  const target = f('Target', 'select', { options: opts(['Self', 'gray'], ['Single', 'blue'], ['Area', 'orange'], ['Line', 'teal']) });
  const effect = f('Effect', 'text');
  const icon = f('Icon', 'relation', rel(links.assets, false));
  return {
    name: 'Abilities',
    icon: 'i:sparkles',
    color: 'purple',
    titleLabel: 'Ability',
    description: 'Spells, skills and passives with their numbers.',
    fields: [type, element, damage, cooldown, mana, radius, target, effect, icon],
    views: galleryAndTable(icon.id, [{ name: 'By element', type: 'board', groupBy: element.id }]),
  };
}

export function itemsCollection(links: PresetLinks = {}): NewCollection {
  const rarity = f('Rarity', 'select', { options: rarityOptions() });
  const slot = f('Slot', 'select', { options: opts(['Weapon', 'red'], ['Armor', 'blue'], ['Trinket', 'purple'], ['Consumable', 'green'], ['Material', 'brown']) });
  const value = f('Value', 'number', { suffix: ' g' });
  const damage = f('Damage', 'dice');
  const weight = f('Weight', 'number', { decimals: 1, suffix: ' kg' });
  const effect = f('Effect', 'text');
  const stack = f('Stackable', 'checkbox');
  const icon = f('Icon', 'relation', rel(links.assets, false));
  return {
    name: 'Items',
    icon: 'i:gem',
    color: 'blue',
    titleLabel: 'Item',
    description: 'Loot, gear and consumables.',
    fields: [rarity, slot, value, damage, weight, effect, stack, icon],
    views: galleryAndTable(icon.id, [{ name: 'By rarity', type: 'board', groupBy: rarity.id }]),
  };
}
